import { useEffect, useState } from "react";
import { getCatalogPayers, type IPayerCapability } from "../services/api";
import { catalogPayerDisplayName } from "../utils/catalog";

/**
 * Module-level cache of catalog payers per (destination, payout method). The
 * claim pins a single payer, so the list is fetched once and reused by any
 * screen that needs to name it.
 */
const cache = new Map<string, IPayerCapability[]>();
const inflight = new Map<string, Promise<IPayerCapability[]>>();

const fetchPayers = (
  key: string,
  destinationCountryCode: string,
  payoutMethod: string | undefined
): Promise<IPayerCapability[]> => {
  const cached = cache.get(key);
  if (cached) return Promise.resolve(cached);

  const pending = inflight.get(key);
  if (pending) return pending;

  const promise = getCatalogPayers({
    destinationCountryCode,
    payoutMethod,
    includeUnavailable: true,
  })
    .then((items) => {
      cache.set(key, items);
      inflight.delete(key);
      return items;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });

  inflight.set(key, promise);
  return promise;
};

/**
 * Human-readable name for a canonical payer code, resolved from the catalog
 * (`displayName`). Returns empty while catalog loads or the code cannot
 * resolve — never a title-cased or raw payer code.
 */
export const usePayerName = (
  payerCode: string | undefined,
  destinationCountry: string,
  payoutMethod?: string
): string => {
  const [displayName, setDisplayName] = useState<string | null>(null);

  useEffect(() => {
    if (!payerCode || !destinationCountry) {
      setDisplayName(null);
      return;
    }

    const key = `${destinationCountry}|${payoutMethod ?? ""}`;
    let active = true;

    fetchPayers(key, destinationCountry, payoutMethod)
      .then((items) => {
        if (!active) return;
        const foundName = catalogPayerDisplayName(payerCode, items);
        setDisplayName(foundName);
      })
      .catch(() => {
        if (active) setDisplayName(null);
      });

    return () => {
      active = false;
    };
  }, [payerCode, destinationCountry, payoutMethod]);

  if (!payerCode) return "";
  return displayName ?? "";
};
